import { useLanguage } from '../context/LanguageContext';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, Tag } from 'lucide-react';
import { publicApi } from '../context/AuthContext';
import useSEO from '../hooks/useSEO';
import useSEOPage from '../hooks/useSEOPage';

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeFilter, setActiveFilter] = useState('Tous');
  const [loading, setLoading] = useState(true);
  const { t, tc, language } = useLanguage();

  const { seoData } = useSEOPage('blog');
  
  useSEO({
    title: seoData?.meta_title,
    description: seoData?.meta_description,
    ogImage: seoData?.og_image,
    url: 'https://joeycyan.com/blog'
  });

  useEffect(() => {
    fetchCategories();
    fetchPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const fetchCategories = async () => {
    try {
      const { data } = await publicApi.get('/blog/categories');
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchPosts = async (category = null) => {
    try {
      const langParam = language !== 'fr' ? `lang=${language}` : '';
      let url = category ? `/blog/posts?category=${category}` : '/blog/posts';
      if (langParam) {
        url += (url.includes('?') ? '&' : '?') + langParam;
      }
      const { data } = await publicApi.get(url);
      setPosts(data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterClick = (filter) => {
    setActiveFilter(filter);
    if (filter === 'Tous' || filter === t('blogPage.allCategories')) {
      fetchPosts();
    } else {
      fetchPosts(filter);
    }
  };

  const getCategoryIcon = (categorySlug) => {
    // Chercher l'emoji dans les catégories chargées
    const category = categories.find(c => c.slug === categorySlug);
    if (category && category.icon) {
      return category.icon;
    }
    
    // Fallback sur les icônes par défaut
    const defaultIcons = {
      'conseils': '💡',
      'technologie': '⚡',
      'actualités': '📰',
      'actualites': '📰',
      'style': '✨',
      'santé visuelle': '👁️',
      'sante-visuelle': '👁️',
      'guide utilisation': '📖',
      'guide-utilisation': '📖'
    };
    return defaultIcons[categorySlug.toLowerCase()] || '📄';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#00aaff] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-5xl font-black mb-4">{t('blogPage.title')} Joey Cyan</h1>
          <p className="text-xl text-gray-300 max-w-3xl">
            {t('blogPage.subtitle')}
          </p>

          {/* Category Icons */}
          <div className="flex flex-wrap gap-6 mt-8">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-2xl">👁️</span>
              <span>{tc('Santé visuelle')}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-2xl">✨</span>
              <span>{tc('Style')}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-2xl">📖</span>
              <span>{tc('Guide utilisation')}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-2xl">⚡</span>
              <span>{tc('Technologie')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handleFilterClick('Tous')}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                activeFilter === 'Tous'
                  ? 'bg-[#00aaff] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t('blogPage.allCategories')}
            </button>
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleFilterClick(cat.slug)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all flex items-center gap-1.5 ${
                  activeFilter === cat.slug
                    ? 'bg-[#00aaff] text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <span>{getCategoryIcon(cat.slug)}</span>
                {tc(cat.name)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Articles Grid */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        {posts.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-500 text-lg">Aucun article trouvé dans cette catégorie.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all transform hover:scale-105"
              >
                {/* Featured Image */}
                <div className="aspect-video overflow-hidden bg-gray-200">
                  <img
                    src={post.image || post.featured_image || '/api/uploads/products/placeholder.png'}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Category Badge */}
                  <div className="flex items-center gap-2 mb-3">
                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 text-xs font-semibold rounded-full">
                      {getCategoryIcon(post.category)}
                      {tc(categories.find(c => c.slug === post.category)?.name || post.category)}
                    </span>
                    {post.reading_time && (
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <Clock size={12} />
                        {post.reading_time} min
                      </span>
                    )}
                  </div>

                  {/* Title */}
                  <h2 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#00aaff] transition-colors line-clamp-2">
                    {post.title}
                  </h2>

                  {/* Excerpt */}
                  <p className="text-sm text-gray-600 line-clamp-3 mb-4">
                    {post.excerpt}
                  </p>

                  {/* Read More */}
                  <div className="text-[#00aaff] font-semibold text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                    {t('blogPage.readMore')}
                    <span>→</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
